import { euro, fromCents, toCents } from '../core/format/currency'
import { brandColors } from '../data/brandColors'
import { SHIPPING_VAT_RATE, exVat, vatRateOf } from '../core/rules/revenue'
import { formatStreet } from '../core/rules/contact'
import { escapeHtml } from '../core/format/html'
import { orderTotal } from './account'
import type { AccountOrder, Customer } from '../core/types/account'
import type { Product } from '../core/types/catalog'

/** De zakelijke gegevens bovenaan elk document (Instellingen › Salon & contact). */
export type DocumentSalon = { name: string; address: string; email: string; kvk: string; vatId?: string; iban?: string; invoicePrefix?: string }
type Salon = DocumentSalon

/** KvK, btw-id en IBAN, elk alleen als het er is. `sep` = tussen de regels (`<br>` of ` · `). */
export const businessIdsHtml = (s: DocumentSalon, sep: string) =>
  [`KvK ${escapeHtml(s.kvk)}`, s.vatId ? `Btw-id ${escapeHtml(s.vatId)}` : '', s.iban?.trim() ? `IBAN ${escapeHtml(s.iban.trim())}` : ''].filter(Boolean).join(sep)

/** Het factuurnummer: het voorvoegsel uit de instellingen plus het doorlopende nummer. */
export const invoiceNumberOf = (s: Pick<DocumentSalon, 'invoicePrefix'>, number: string) => `${s.invoicePrefix?.trim() ?? ''}${number}`

/** Tekst veilig in losse HTML zetten (factuur, pakbon): één definitie in core. */
export { escapeHtml }
const esc = escapeHtml

type PostalAddress = { street: string; houseNumber: string; postcode: string; city: string; country?: string }

/** Straat, postcode en plaats (en "België") als losse HTML-regels, veilig ge-escaped. Eén vorm voor factuur en pakbon. */
export const addressHtml = (a: PostalAddress) =>
  `${esc(formatStreet(a))}<br>${esc(`${a.postcode} ${a.city}`.trim())}${a.country === 'BE' ? '<br>België' : ''}`

/** Hoe lang het printframe blijft staan als de browser geen `afterprint` stuurt. */
const CLEANUP_MS = 60_000

/** De factuur als losse HTML (geen script: werkt ook onder een strikte CSP). */
export function invoiceHtml(
  order: AccountOrder,
  customer: Customer,
  salon: Salon,
  productById: (id: string) => Product | undefined,
  dateLabel: string,
): string {
  const lines = order.lines.map((l) => {
    const p = productById(l.productId)
    // Altijd de betaalde prijs en het bewaarde tarief, nooit de huidige: een factuur verandert niet achteraf.
    const cents = toCents(l.price) * l.qty
    return { name: p?.name ?? l.productId, qty: l.qty, total: fromCents(cents), cents, vatRate: l.vatRate ?? vatRateOf(p ?? {}) }
  })
  // Optellen in centen: anders kan het subtotaal een cent afwijken van de som van de regels.
  const subtotal = fromCents(lines.reduce((s, l) => s + l.cents, 0))
  const total = orderTotal(order)
  const vat = vatPerRate([...lines, { cents: toCents(order.shipping), vatRate: SHIPPING_VAT_RATE }])
  const money = (n: number) => esc(euro(n, { cents: true }))
  const number = invoiceNumberOf(salon, order.number || order.id)

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>Factuur ${esc(number)}</title>
<style>
@page{margin:16mm}
body{font-family:Inter,system-ui,sans-serif;color:${brandColors.ink};margin:48px;font-size:14px;line-height:1.5}
@media print{body{margin:0}}
h1{font-family:Fraunces,Georgia,serif;font-weight:300;font-size:32px;margin:0 0 4px}
.muted{color:${brandColors.muted}}.row{display:flex;justify-content:space-between;gap:24px}
table{width:100%;border-collapse:collapse;margin-top:32px}th,td{text-align:left;padding:10px 0;border-bottom:1px solid ${brandColors.line}}
th{font-weight:400;color:${brandColors.muted}}td.r,th.r{text-align:right}.tot td{border:0;padding:4px 0}.big td{font-size:16px;padding-top:12px}
</style></head><body>
<div class="row"><div><h1>Factuur</h1><div class="muted">${esc(number)} · ${esc(dateLabel)}</div></div>
<div style="text-align:right"><strong>${esc(salon.name)}</strong><br>${esc(salon.address)}<br>${esc(salon.email)}<br>${businessIdsHtml(salon, '<br>')}</div></div>
<p style="margin-top:32px">${esc(`${customer.firstName} ${customer.lastName}`.trim())}<br>${addressHtml(customer.address)}</p>
<table><thead><tr><th>Product</th><th class="r">Aantal</th><th class="r">Bedrag</th></tr></thead><tbody>
${lines.map((l) => `<tr><td>${esc(l.name)}</td><td class="r">${l.qty}</td><td class="r">${money(l.total)}</td></tr>`).join('')}
</tbody></table>
<table><tbody class="tot">
<tr><td class="muted">Subtotaal</td><td class="r">${money(subtotal)}</td></tr>
<tr><td class="muted">Verzending</td><td class="r">${order.shipping ? money(order.shipping) : 'Gratis'}</td></tr>
${vat.map((v) => `<tr><td class="muted">Waarvan btw ${v.vatRate}%</td><td class="r">${money(fromCents(v.vatCents))}</td></tr>`).join('')}
<tr class="big"><td>Totaal</td><td class="r">${money(total)}</td></tr>
</tbody></table>
<p class="muted" style="margin-top:48px">Betaald. Dank je wel voor je bestelling.</p>
</body></html>`
}

/**
 * Btw per tarief over regels en verzending (in centen), zoals de factuur in het beheer. 0% (een
 * cadeaubon) staat er niet op: daar zit geen btw in.
 */
function vatPerRate(parts: { cents: number; vatRate: number }[]): { vatRate: number; vatCents: number }[] {
  const gross = new Map<number, number>()
  parts.forEach((p) => gross.set(p.vatRate, (gross.get(p.vatRate) ?? 0) + p.cents))
  return [...gross.entries()]
    .filter(([rate, cents]) => rate > 0 && cents !== 0)
    .sort(([a], [b]) => b - a)
    .map(([vatRate, cents]) => ({ vatRate, vatCents: cents - exVat(cents, vatRate) }))
}

/** Valt printen niet te starten, dan de factuur als bestand downloaden. Een download is nooit een pop-up. */
function downloadHtml(html: string, filename: string) {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), CLEANUP_MS)
}

/**
 * Drukt de factuur af via een onzichtbaar frame op de eigen pagina, zodat de klant hem
 * als PDF kan bewaren.
 *
 * Geen `window.open` meer: Safari en andere browsers blokkeren een nieuw venster zodra er
 * iets asynchroons (een API-verzoek) tussen de klik en het openen zit. `print()` op een frame
 * valt niet onder de pop-upblokkering. Lukt printen toch niet, dan downloaden we de HTML.
 *
 * Met de backend hoort hier een echte PDF van de server: een gewone `<a href download>`
 * naar die URL, zonder JavaScript-tussenstap. Dat is de meest robuuste vorm.
 */
export function printInvoice(...args: Parameters<typeof invoiceHtml>): Promise<boolean> {
  return printHtml(invoiceHtml(...args), `factuur-${invoiceNumberOf(args[2], args[0].number || args[0].id)}.html`, 'Factuur')
}

/** Losse HTML afdrukken via een onzichtbaar frame (zie hierboven); valt terug op downloaden. */
export function printHtml(html: string, filename: string, title: string): Promise<boolean> {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.tabIndex = -1
    frame.title = title
    // Buiten beeld maar wél gerenderd: `display:none` laat sommige browsers een lege pagina printen.
    Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0', opacity: '0' })

    let removed = false
    const cleanup = () => {
      if (removed) return
      removed = true
      frame.remove()
    }

    frame.onload = () => {
      const win = frame.contentWindow
      try {
        if (!win) throw new Error('Geen printframe')
        win.addEventListener('afterprint', () => setTimeout(cleanup, 0), { once: true })
        win.focus()
        win.print()
        setTimeout(cleanup, CLEANUP_MS)
        resolve(true)
      } catch {
        cleanup()
        try {
          downloadHtml(html, filename)
          resolve(true)
        } catch {
          resolve(false)
        }
      }
    }
    frame.srcdoc = html
    document.body.appendChild(frame)
  })
}
